import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const ContactSection = () => {
  const { toast } = useToast();
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    website: "", // Honeypot field for bot detection
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== FORM SUBMISSION STARTED ===');
    console.log('Form state:', formState);
    console.log('Supabase client:', supabase);
    setIsSubmitting(true);
    
    try {
      console.log('=== INVOKING CONTACT FUNCTION ===');
      const { data, error } = await supabase.functions.invoke('contact-submission', {
        body: formState
      });

      console.log('=== FUNCTION RESPONSE ===');
      console.log('Data:', data);
      console.log('Error:', error);

      if (error) {
        console.error('=== FUNCTION ERROR ===', error);
        throw error;
      }

      setIsSubmitted(true);
      setFormState({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
        website: "",
      });

      toast({
        title: "Message sent successfully!",
        description: "We'll contact you within 24 hours.",
      });
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error sending message",
        description: "Please try again or contact us directly at info@quietcorner.io",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold text-quietcove-blue mb-6">
              Transform Your Space with Quiet Corner
            </h2>
            <p className="text-quietcove-grey text-lg mb-8">
              Ready to unlock new revenue with our elegant, smart workspace pods? Contact us to discuss how Quiet Corner can integrate seamlessly into your property.
            </p>
            
            <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
              <h3 className="text-xl font-semibold text-quietcove-blue mb-4">
                What Happens Next
              </h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <div className="bg-quietcove-green/10 text-quietcove-green rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-quietcove-blue">Initial Consultation</p>
                    <p className="text-sm text-quietcove-grey">
                      We'll contact you within 24 hours to schedule a call and understand your space.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="bg-quietcove-green/10 text-quietcove-green rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-quietcove-blue">Space Assessment</p>
                    <p className="text-sm text-quietcove-grey">
                      Our team will evaluate your location to determine optimal pod placement.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="bg-quietcove-green/10 text-quietcove-green rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-quietcove-blue">Custom Proposal</p>
                    <p className="text-sm text-quietcove-grey">
                      We'll provide a detailed plan including revenue projections and implementation timeline.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-quietcove-blue/80 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-white">Direct Contact</h3>
              <p className="mb-4 text-white">
                Prefer to speak directly? Reach out to our partnerships team:
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">info@quietcorner.io</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-md border border-quietcove-oak/10">
            <h3 className="text-2xl font-semibold text-quietcove-blue mb-6">
              Get in Touch About Your Space
            </h3>
            
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="bg-quietcove-green/10 p-4 rounded-full mb-4">
                  <Check className="w-10 h-10 text-quietcove-green" />
                </div>
                <h4 className="text-xl font-semibold text-quietcove-blue mb-2">
                  Thank You!
                </h4>
                <p className="text-quietcove-grey mb-4">
                  Your message has been received. A member of our team will contact you within 24 hours.
                </p>
                <Button 
                  className="bg-quietcove-blue hover:bg-quietcove-blue/90 text-white mt-2"
                  onClick={() => setIsSubmitted(false)}
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, visible to bots */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <Input
                    type="text"
                    name="website"
                    value={formState.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-quietcove-blue">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      required
                      className="border-quietcove-sand focus:border-quietcove-green focus:ring-quietcove-green/10"
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-quietcove-blue">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formState.email}
                      onChange={handleChange}
                      required
                      className="border-quietcove-sand focus:border-quietcove-green focus:ring-quietcove-green/10"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-medium text-quietcove-blue">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formState.phone}
                      onChange={handleChange}
                      className="border-quietcove-sand focus:border-quietcove-green focus:ring-quietcove-green/10"
                      placeholder=""
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-medium text-quietcove-blue">
                      Company/Property Name *
                    </label>
                    <Input
                      id="company"
                      name="company"
                      value={formState.company}
                      onChange={handleChange}
                      required
                      className="border-quietcove-sand focus:border-quietcove-green focus:ring-quietcove-green/10"
                      placeholder="Meridian Hotel"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium text-quietcove-blue">
                    Tell us about your space *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    required
                    className="min-h-[120px] border-quietcove-sand focus:border-quietcove-green focus:ring-quietcove-green/10"
                    placeholder="Tell us about your property, the available space, and any questions you have."
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-quietcove-green hover:bg-quietcove-green/90 text-white py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Contact Us About Your Space"
                  )}
                </Button>
                
                <p className="text-xs text-quietcove-grey text-center mt-4">
                  By submitting this form, you agree to our{" "}
                  <a href="#" className="text-quietcove-blue underline">
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
